import { OrderData } from '../types/orderFormTypes';
import { parseClienteNetoFlag } from './clienteNetoFlag';
import { buildOrderCouponLines, type OrderCouponLine } from './orderCouponLines';
import {
  getOrderGrandTotalForPayload,
  getShippingCostForOrderPayload
} from './orderFormUtils';

export interface TransformedOrderData {
  billing: {
    address_1: string;
    address_2: string;
    city: string;
    company: string;
    country: string;
    email: string;
    first_name: string;
    last_name: string;
    phone: string;
    postcode: string;
    second_last_name: string;
    second_name: string;
    state: string;
  };
  shipping: {
    address_1: string;
    address_2: string;
    city: string;
    company: string;
    country: string;
    first_name: string;
    last_name: string;
    phone: string;
    postcode: string;
    second_last_name: string;
    second_name: string;
    state: string;
  };
  line_items: Array<{
    product_id: string;
    quantity: number;
    price: string;
    tax_rate: string;
    name: string;
    sku: string;
    total: string;
    discount: string;
    meta_data: Array<{
      key: string;
      value: string;
    }>;
    custom_kit_products?: Array<{
      sku: string;
      precio: string;
      iva: number; // Porcentaje de IVA como entero (ej: 19)
      name: string;
    }>;
  }>;
  meta_data: Array<{
    key: string;
    value: string;
  }>;
  status?: string;
  payment_method: string;
  payment_method_title: string;
  total: string;
  shipping_total: string;
  currency: string;
  store_name: string;
  guia: string;
  date_created: string;
  date_modified: string;
  orders_id: string;
  id?: string;
  origin_order_id?: string;
  pagos?: Array<{
    metodo_pago: string;
    valor: number;
    id_transaccion?: string | null;
    dcto?: string | null;
    forma_pago?: string;
    plazo?: number;
    pago_verificado?: number;
    fecha_creacion?: string;
  }>;
  old_data_order?: {
    status?: string;
    customer_note?: string;
    lineItems?: Array<{
      product_id: string;
      product_name: string;
      quantity: number;
      price: string;
      stock?: number;
      discount?: number;
      name?: string;
    }>;
      transaction_id?: string;
  };
  coupon_lines?: OrderCouponLine[];
}

export const transformOrderData = (formData: OrderData): TransformedOrderData => {
  const pagosExtraDetails = formData.extraDetails?.pagos
  const plazoValue =
    formData.extraDetails?.plazo ??
    (Array.isArray(pagosExtraDetails) && pagosExtraDetails.length > 0
      ? pagosExtraDetails[0]?.plazo
      : '')
  // Log para verificar que los datos se estén enviando correctamente
  if (formData.old_data_order) {
    console.log('Datos de auditoría enviados:', {
      old_data_order: formData.old_data_order
    });
  } else if (formData.id) {
    console.log('Modo edit - enviando old_data_order vacío:', {
      old_data_order: {}
    });
  } else {
    console.log('Modo create - enviando old_data_order con estado "crear":', {
      old_data_order: { status: "crear" }
    });
  }

  // Log específico para pagos parciales
  if (
    (formData.extraDetails?.payment_method === 'part' || formData.extraDetails?.payment_method === 'plan_separe') &&
    formData.extraDetails?.pagos
  ) {
    console.log('Pagos parciales enviados:', formData.extraDetails.pagos);
  }

  // Calcular forma_pago automáticamente si falta pero hay payment_method (respaldo)
  // Esto asegura que siempre se envíe un valor válido a la API
  let formaPagoFinal = formData.extraDetails?.forma_pago
  if (!formaPagoFinal && formData.extraDetails?.payment_method) {
    const paymentMethod = formData.extraDetails.payment_method
    if (paymentMethod === 'cod' || paymentMethod === 'cmdm' || paymentMethod === 'addi' || paymentMethod === 'plan_separe') {
      formaPagoFinal = '4' // Crédito/Contra entrega
    } else if (paymentMethod === 'bold') {
      formaPagoFinal = '6' // Contado
    } else {
      formaPagoFinal = '6' // Por defecto contado
    }
    console.log(`🔄 forma_pago calculado automáticamente: ${formaPagoFinal} (payment_method: ${paymentMethod})`)
  }
  
  // Log para verificar forma_pago antes de enviar
  console.log('📤 forma_pago que se enviará a la API:', formaPagoFinal || formData.extraDetails?.forma_pago || 'NO DEFINIDO')

  const clienteNeto = parseClienteNetoFlag(formData.extraDetails?.cliente_neto)
  const shippingGrossUser = formData.extraDetails?.costo_envio || '0'
  const shippingForApi = getShippingCostForOrderPayload(shippingGrossUser, clienteNeto)
  const orderGrandTotal = getOrderGrandTotalForPayload(
    formData.lineItems || [],
    formData.productDetails || {},
    shippingGrossUser,
    clienteNeto
  )

  const transformedData: TransformedOrderData = {
    billing: {
      address_1: formData.billingAddress?.address_1 || '',
      address_2: formData.billingAddress?.address_2 || '',
      city: formData.billingAddress?.city || '',
      company: '',
      country: 'CO',
      email: formData.customer?.email || '',
      first_name: formData.customer?.first_name || '',
      last_name: formData.customer?.last_name || '',
      phone: formData.billingAddress?.phone || '',
      postcode: '',
      second_last_name: '',
      second_name: '',
      state: formData.billingAddress?.state || ''
    },
    shipping: {
      address_1: formData.shippingAddress?.address_1 || '',
      address_2: formData.shippingAddress?.address_2 || '',
      city: formData.shippingAddress?.city || '',
      company: '',
      country: 'CO',
      first_name: formData.customer?.first_name || '',
      last_name: formData.customer?.last_name || '',
      phone: formData.shippingAddress?.phone || formData.billingAddress?.phone || '',
      postcode: '',
      second_last_name: '',
      second_name: '',
      state: formData.shippingAddress?.state || ''
    },
    line_items: formData.lineItems?.map(item => {
      const productDetail = formData.productDetails?.[item.sku || item.product_id];
      
      // Obtener el IVA correctamente desde productDetails
      const getIvaValue = () => {
        // Prioridad: iva_ofima del productDetail, luego item.iva, luego 19% por defecto
        if (productDetail?.iva_ofima !== undefined && productDetail?.iva_ofima !== null) {
          return productDetail.iva_ofima.toString();
        }
        if (item.iva !== undefined && item.iva !== null) {
          return item.iva.toString();
        }
        return '19'; // Valor por defecto del 19%
      };

      const ivaValue = getIvaValue();

      const lineItem: any = {
        product_id: item.product_id,
        quantity: item.quantity,
        price: item.price,
        tax_rate: ivaValue,
        name: item.product_name,
        sku: item.sku || productDetail?.sku || item.product_id,
        subtotal: (parseFloat(item.price) * item.quantity).toString(),
        total: (parseFloat(item.price) * item.quantity).toString(),
        discount: (item.discount || 0).toString(),
        meta_data: [
          {
            key: 'product_iva',
            value: ivaValue
          }
        ]
      };

      return lineItem;
    }) || [],
    meta_data: [
      {
        key: 'order_agente',
        value: formData.agente?.toString() || ''
      },
      {
        key: 'tipo_lead',
        value: formData.extraDetails?.tipo_lead || ''
      },
      {
        key: 'tipo_cliente',
        value: formData.extraDetails?.tipo_cliente || ''
      },
      {
        key: 'marca',
        value: formData.extraDetails?.marca || ''
      },
      {
        key: 'banco',
        value: formData.extraDetails?.banco || ''
      },
      {
        key: 'transportadora',
        value: formData.extraDetails?.transportadora || ''
      },
      {
        key: 'conocer',
        value: formData.extraDetails?.conocer || ''
      },
      {
        key: 'id_pago',
        value: formData.extraDetails?.id_pago || ''
      },
      {
        key: 'forma_pago',
        value: formaPagoFinal || formData.extraDetails?.forma_pago || '6'
      },
      {
        key: 'payment_method',
        value: formData.extraDetails?.payment_method || ''
      },
      {
        key: 'plazo',
        value: plazoValue != null ? String(plazoValue) : ''
      },
      {
        key: 'transaction_id',
        value: formData.extraDetails?.transaction_id || ''
      },
      {
        key: 'destino_pedido',
        value: formData.extraDetails?.destino_pedido || ''
      },
      {
        key: 'ciudad_origen',
        value: formData.billingAddress?.city || '',
      },
      {
        key: 'ciudad_destino',
        value: formData.shippingAddress?.city || '',
      },
      {
        key: 'store_name',
        value: formData.store_name || ''
      },
      {
        key: 'costo_envio',
        value: formData.status === 'guiaeliminada' ? '' : shippingForApi
      },
     /*  {
        key: 'estado_envio',
        value: formData.extraDetails?.estado_envio || ''
      },
      {
        key: 'valor_liquidado',
        value: formData.extraDetails?.valor_liquidado || '0'
      },
      {
        key: 'servicio_transportadora',
        value: formData.extraDetails?.servicio_transportadora || '0'
      }, */
      {
        key: 'customer_document',
        value: formData.customer?.document || ''
      },
      {
        key: 'billing_cedula',
        value: formData.customer?.document || ''
      },
      {
        key: 'customer_tipo_documento',
        value: formData.customer?.tipo_documento || ''
      },
      {
        key: 'customer_note',
        value: formData.extraDetails?.customer_note || ''
      },
      {
        key: 'retencion_fuente_activa',
        value: formData.extraDetails?.retencion_fuente_activa ? '1' : '0'
      },
      {
        key: 'retencion_fuente',
        value:
          formData.extraDetails?.retencion_fuente_activa &&
          formData.extraDetails?.retencion_fuente != null &&
          formData.extraDetails?.retencion_fuente !== ''
            ? String(Math.round(Number(formData.extraDetails.retencion_fuente) || 0))
            : ''
      },
      {
        key: 'cliente_neto',
        value: parseClienteNetoFlag(formData.extraDetails?.cliente_neto) ? '1' : '0'
      },
      {
        key: 'recoge_en_bodega',
        value: formData.meta_data?.recoge_en_bodega?.toString() || '0'
      },
      {
        key: 'id_market',
        value: (formData.meta_data?.id_market ?? '').toString()
      },
      {
        key: 'fecha_entrega',
        value: (formData.meta_data?.fecha_entrega ?? '').toString()
      },
      ...(formData.cotizacion_id ? [{
        key: 'cotizacion_id',
        value: formData.cotizacion_id
      }] : [])
    ],
    status:
      formData.extraDetails?.payment_method === 'plan_separe'
        ? 'verify_transaction'
        : (formData.status || 'pending'),
    payment_method: formData.extraDetails?.payment_method || 'cod',
    payment_method_title: 'Pago en efectivo - Contra entrega',
    total: orderGrandTotal,
    shipping_total: formData.status === 'guiaeliminada' ? '' : shippingForApi,
    store_name: formData.store_name || '',
    currency: 'COP',
    orders_id: formData.orders_id || '',
    guia: formData.status === 'guiaeliminada' ? '' : formData.guia || '',
    date_created: formData.date_created || '',
    date_modified: formData.date_modified || '',
    ...(formData.id ? { id: formData.id.toString() } : {}),
    // ID de origen solo para HomeCenter y MercadoLibre en modo create
    ...((formData.store_name === 'Homecenter' || formData.store_name === 'Mercado Libre') && !formData.id && formData.extraDetails?.id_origen ? {
      id: formData.extraDetails.id_origen
    } : {}),
    // origin_order_id solo para MercadoLibre en modo create
    ...(formData.store_name === 'Mercado Libre' && formData.extraDetails?.seguimiento ? {
      origin_order_id: formData.extraDetails.seguimiento
    } : {}),
    ...((formData.extraDetails?.payment_method === 'part' || formData.extraDetails?.payment_method === 'plan_separe') && formData.extraDetails?.pagos ? {
      pagos: formData.extraDetails.pagos.map(pago => ({
        ...pago,
        forma_pago: pago.forma_pago || formaPagoFinal || '6'
      }))
    } : {}),
    ...(formData.old_data_order ? { 
      old_data_order: formData.old_data_order // Mantener el campo en inglés
    } : formData.id ? { 
      old_data_order: {} // En modo edit siempre enviar old_data_order, aunque esté vacío
    } : {
      old_data_order: { status: "crear" } // En modo create enviar con estado "crear"
    }),
    coupon_lines: buildOrderCouponLines(formData)
  };
  
  // Log final para verificar que old_data_order está en el payload
  if (transformedData.old_data_order) {
    console.log('✅ old_data_order incluido en payload:', JSON.stringify(transformedData.old_data_order));
  } else {
    console.warn('⚠️ old_data_order NO está en el payload');
  }
  
  return transformedData;
}; 